import { useMemo, useState } from "react";
import { IconFilter, IconRefresh } from "@tabler/icons-react";
import { BotonExportarExcel, Button, Group, IconAction, PageHeader, PaginadorTabla, Paper, Stack, Text } from "@/components/ui";
import { useServerPagination } from "@/lib/hooks/use-server-pagination";
import type { ColumnaExcel } from "@/shared/export/exportar-excel";
import { useMisFacturas } from "../hooks/use-mis-facturas";
import { listarMisFacturas, type Factura } from "../services/facturas.api";
import { aParamsServer, type FacturaFiltros, type FacturaSort } from "../utils/ordenar-facturas";
import { FacturasTable } from "./FacturasTable";
import { ENCABEZADO_MIS_FACTURAS, MisFacturasSkeleton } from "./MisFacturasSkeleton";

const COLUMNAS_EXCEL: ColumnaExcel<Factura>[] = [
  { header: "Solicitud", value: (f) => f.folioSolicitud },
  { header: "UUID", value: (f) => f.uuid.toUpperCase() },
  { header: "Emisor", value: (f) => f.nombreEmisor },
  { header: "RFC emisor", value: (f) => f.rfcEmisor },
  { header: "Receptor", value: (f) => f.nombreReceptor },
  { header: "RFC receptor", value: (f) => f.rfcReceptor },
  { header: "Fecha validación", value: (f) => f.fechaValidacion },
  { header: "Fecha timbrado", value: (f) => f.fechaTimbrado },
  { header: "Fecha emisión", value: (f) => f.fechaEmision },
  { header: "Total", value: (f) => f.total },
  { header: "Moneda", value: (f) => f.moneda },
  { header: "Estatus", value: (f) => f.estatus },
  { header: "Serie", value: (f) => f.serie },
  { header: "Folio", value: (f) => f.folio },
  { header: "Orden de compra", value: (f) => f.ordenCompraClave },
  { header: "Tipo", value: (f) => f.tipoComprobante },
  { header: "Observaciones", value: (f) => f.observaciones },
  { header: "Núm. documento", value: (f) => f.numeroSolicitud },
];

/**
 * "Mis facturas" (P30) · los acuses de validación de los CFDIs cargados por el
 * solicitante actual, con estatus Válido/No válido/Cancelado/En proceso y
 * descarga del XML original + acuse en PDF. Filtro por columna (fila de
 * búsqueda bajo los encabezados · mismo patrón que Mis solicitudes), orden por
 * columna y PAGINADO — todo resuelto en el SERVIDOR (con debounce); el filtro
 * también se aplica en cliente sobre la página visible para feedback
 * instantáneo mientras llega la respuesta.
 */
export function MisFacturasPage() {
  // Estado de tabla server-side (F1 · página + filtros debounced + reset a pág. 1).
  const { filtros, setFiltros, filtrosDebounced, page, setPage, pageSize, filtrosActivos } =
    useServerPagination<FacturaFiltros>({});
  const [sort, setSort] = useState<FacturaSort | null>(null);
  // Fila de filtros por columna COLAPSADA por default (mismo patrón que Mis
  // solicitudes) · el botón "Filtros" la muestra/oculta y trae el contador.
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  const serverParams = useMemo(
    () => ({ ...aParamsServer(filtrosDebounced, sort), page, pageSize }),
    [filtrosDebounced, sort, page, pageSize],
  );

  const { data, isPending, isError, error, refetch, isFetching, isPlaceholderData } =
    useMisFacturas(serverParams);
  const facturas = data?.items ?? [];
  const total = data?.total ?? 0;

  // Primera carga (sin datos · `keepPreviousData` evita volver aquí al filtrar o
  // paginar) · MISMO skeleton que el `pendingComponent` de la ruta.
  if (isPending) return <MisFacturasSkeleton />;

  return (
    <div className="w-full px-4 py-6 md:px-6">
      <PageHeader
        title={ENCABEZADO_MIS_FACTURAS.titulo}
        description={ENCABEZADO_MIS_FACTURAS.descripcion}
        actions={
          <>
            <BotonExportarExcel
              archivo="mis-facturas"
              hoja="Facturas"
              columnas={COLUMNAS_EXCEL}
              obtenerFilas={async () => {
                // Todas las páginas con los filtros/orden actuales.
                const params = aParamsServer(filtrosDebounced, sort);
                const todas: Factura[] = [];
                for (let p = 1; ; p++) {
                  const pagina = await listarMisFacturas({ ...params, page: p, pageSize: 100 });
                  todas.push(...pagina.items);
                  if (todas.length >= pagina.total || pagina.items.length === 0) break;
                }
                return todas;
              }}
            />
            <IconAction label="Refrescar" variant="default" size="lg" onClick={() => refetch()} loading={isFetching}>
              <IconRefresh size={16} />
            </IconAction>
          </>
        }
      />
      <Stack gap="md">
        {isError ? (
          <Paper padding="lg">
            <Text c="peligro-600" size="sm">
              Error: {(error as Error).message}
            </Text>
          </Paper>
        ) : (
          <>
            {/* Paginación STICKY · se mantiene visible bajo el header fijo (56px)
                al hacer scroll. El fondo opaco cubre el contenido que pasa por
                debajo; el bleed lateral (-mx/px) la extiende de borde a borde. */}
            <div className="bg-pagina sticky top-[var(--shell-header-h)] z-20 -mx-4 px-4 py-2 md:-mx-6 md:px-6">
              <Group justify="space-between" align="center" gap="sm" wrap="wrap">
                <Button
                  size="sm"
                  variant={mostrarFiltros ? "light" : "default"}
                  leftSection={<IconFilter size={14} stroke={1.8} />}
                  onClick={() => setMostrarFiltros((v) => !v)}
                  aria-pressed={mostrarFiltros}
                  aria-label={mostrarFiltros ? "Ocultar fila de filtros" : "Mostrar fila de filtros"}
                >
                  Filtros{filtrosActivos > 0 ? ` · ${filtrosActivos}` : ""}
                </Button>
                <PaginadorTabla
                  total={total}
                  page={page}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  variante="inferior"
                  sustantivo="facturas"
                />
              </Group>
            </div>
            <FacturasTable
              facturas={facturas}
              filtros={filtros}
              sort={sort}
              buscando={isPlaceholderData}
              mostrarFiltros={mostrarFiltros}
              onFiltrar={(key, valor) => setFiltros((prev) => ({ ...prev, [key]: valor }))}
              onOrdenar={setSort}
            />
            <Text size="xs" c="dimmed">
              El estatus "En proceso" se resuelve cuando el SAT confirma la vigencia del
              comprobante.
            </Text>
          </>
        )}
      </Stack>
    </div>
  );
}
